// src/components/InlineEntityImage.tsx
//
// Renders an inline image card for a [IMAGE](category:identifier)
// marker. Looks up Wikipedia's REST API for a thumbnail + caption,
// shows nothing while loading or on failure (graceful degradation).
//
// Layout: small image tile (full-width, capped height ~200px on
// mobile, with a caption below). Tap opens a lightbox modal so the
// user can see the full image. Attribution chip links out to the
// Wikipedia article.

import React, { useState } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  Skeleton,
  Dialog,
  DialogContent,
  IconButton,
  Link,
} from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { useEntityImage } from '../hooks/useEntityImage';
import type { EntityImageRef } from '../utils/interactiveContent';
import { useT } from '../i18n/useT';

interface Props {
  refItem: EntityImageRef;
}

export const InlineEntityImage: React.FC<Props> = ({ refItem }) => {
  const t = useT();
  const image = useEntityImage(refItem.category, refItem.identifier);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Two states we don't render anything for: still loading (image
  // hasn't resolved yet, but we don't know if it'll resolve at all
  // without a separate "loading" flag — going optimistic here, no
  // skeleton, so the prose isn't broken up by phantom placeholders
  // for entities Wikipedia doesn't have). And resolution failure
  // (image === null after fetch).
  //
  // The trade-off: on slow networks the image may "pop in" a few
  // seconds after the prose. That's better than reserving space
  // for an image we may never get.
  if (!image) return null;

  return (
    <Box
      sx={{
        my: 1.5,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <ButtonBase
        onClick={() => setLightboxOpen(true)}
        sx={{
          width: '100%',
          maxWidth: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'block',
          textAlign: 'left',
          bgcolor: 'action.hover',
          cursor: 'zoom-in',
          transition: 'transform 0.05s ease, box-shadow 0.15s ease',
          '&:active': { transform: 'scale(0.997)' },
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          },
        }}
      >
        <Box
          component="img"
          src={image.thumbnailUrl}
          alt={image.title}
          loading="lazy"
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: { xs: 220, sm: 280 },
            objectFit: 'cover',
            // Slight bottom-edge gradient is sometimes nice for
            // legibility of an overlaid caption, but we put the
            // caption BELOW the image to keep the photo unspoiled.
          }}
          onError={(e) => {
            // If Wikipedia returned a thumbnail URL but the image
            // itself fails to load (rare — usually a transient
            // upstream issue), hide the broken icon.
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </ButtonBase>

      {/* Caption + attribution row. Description from Wikipedia is
          typically a one-liner like "German Renaissance painter
          (1471–1528)" — perfect length for inline. */}
      <Box
        sx={{
          mt: 0.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              fontSize: '0.78rem',
              lineHeight: 1.3,
              color: 'text.primary',
            }}
          >
            {image.title}
          </Typography>
          {image.description && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '0.72rem',
                color: 'text.secondary',
                lineHeight: 1.35,
                mt: 0.1,
              }}
            >
              {image.description}
            </Typography>
          )}
        </Box>
        {/* Wikipedia attribution. Required by the licensing — most
            Wikimedia images are CC-BY-SA, so we link out so users
            can see full credit. The icon-only link keeps it
            unobtrusive. */}
        <Link
          href={image.articleUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            fontSize: '0.65rem',
            opacity: 0.75,
            flexShrink: 0,
            '&:hover': { opacity: 1 },
          }}
          title={t('Source: Wikipedia')}
        >
          {t('Wikipedia')}
          <OpenInNew sx={{ fontSize: 11 }} />
        </Link>
      </Box>

      {/* Lightbox modal for full-resolution viewing. Mirrors the
          chat-image lightbox pattern (FeedItemCard / MessageBubble):
          dimmed backdrop, click-anywhere-to-close, top-right close
          button. */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.92)',
            boxShadow: 'none',
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: '95vh' },
          },
        }}
      >
        <DialogContent
          onClick={() => setLightboxOpen(false)}
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            cursor: 'zoom-out',
            minHeight: { xs: '100vh', sm: 'auto' },
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(false);
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'common.white',
              bgcolor: 'rgba(0,0,0,0.4)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              zIndex: 1,
            }}
            aria-label={t('Close')}
          >
            <Close />
          </IconButton>
          <Box
            component="img"
            src={image.fullUrl}
            alt={image.title}
            sx={{
              maxWidth: '100%',
              maxHeight: { xs: '100vh', sm: '95vh' },
              objectFit: 'contain',
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

/** Renders one or more entity image cards stacked vertically.
 *  Component is a pass-through to InlineEntityImage but provides
 *  the loading skeleton for the FIRST image in the list. */
export const InlineEntityImageList: React.FC<{
  refs: EntityImageRef[];
}> = ({ refs }) => {
  if (refs.length === 0) return null;
  return (
    <>
      {refs.map((r, idx) => (
        <InlineEntityImage key={`${r.category}-${r.identifier}-${idx}`} refItem={r} />
      ))}
    </>
  );
};
